import {
	ReactNode,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { audienceSortStepNames } from "./utility/audienceSortStepNames";
import { DDXModal } from "components/ddx-modal/DDXModal";
import { createPortal } from "react-dom";
import { SCREEN_WIDTHS } from "components/utility/constants/screen_widths";

interface AudienceSortModalProps {
	theme: string;
	currentPage: string;
	currentStep: string;
	children: ReactNode;
	isMobile: boolean;
	setStep: React.Dispatch<React.SetStateAction<string>>;
	isModalOpen: boolean;
	setIsModalOpen?: (value: React.SetStateAction<boolean>) => void;
	isWide: boolean;
	modalJustOpened?: boolean;
	className: string;
	preventHandleClickOutside?: boolean;
	additionalCloseLogic?(event?): void;
}

const AudienceSortModal: React.FC<AudienceSortModalProps> = ({
	theme,
	currentPage,
	children,
	currentStep,
	isMobile,
	setStep,
	isModalOpen,
	setIsModalOpen,
	isWide = true,
	modalJustOpened = true,
	className,
	preventHandleClickOutside = false,
	additionalCloseLogic
}) => {
	const audienceSortRef = useRef<HTMLDivElement>(null);
	const [cameFromWide, setCameFromWide] = useState<boolean>(false);

	const handleClickOutsideModal = useCallback(() => {
		if (preventHandleClickOutside) return;
		setStep(audienceSortStepNames.initialStep);
		setIsModalOpen?.(false);
		additionalCloseLogic?.();
	}, [preventHandleClickOutside, setIsModalOpen, setStep, additionalCloseLogic]);

	const pagesWithAnimatedModal: Set<string> = useMemo(() => new Set(["nav", "from-link"]), []);
	const pagesWhereResizeDoesntCloseModal: Set<string> = useMemo(
		() => new Set(["nav", "global-splash"]),
		[]
	);

	const wideModalSteps = useMemo(
		() =>
			new Set([
				audienceSortStepNames.countrySelection,
				audienceSortStepNames.financialProfessionalAffirmation
			]),
		[]
	);

	useEffect(() => {
		if (!pagesWithAnimatedModal.has(currentPage)) return;

		const audienceSortModal = audienceSortRef.current;

		if (audienceSortModal) {
			const audienceSortContent = audienceSortModal.firstElementChild as HTMLDivElement;
			if (
				(audienceSortModal.classList.contains("wide") &&
					wideModalSteps.has(currentStep) &&
					!cameFromWide) ||
				audienceSortModal.classList.contains("narrow")
			) {
				audienceSortContent.style.opacity = "0";
				audienceSortContent.classList.add("fade-in-animation");
			}
			if (audienceSortModal.classList.contains("wide")) {
				setCameFromWide(true);
			} else {
				setCameFromWide(false);
			}
		}
	}, [currentStep, cameFromWide, currentPage, pagesWithAnimatedModal, wideModalSteps]);

	const findModalAnimationClassname = () => {
		if (!pagesWithAnimatedModal.has(currentPage)) return "";
		if (modalJustOpened) return " initial";
		if (isWide) return " wide";
		return " narrow";
	};

	useEffect(() => {
		if (isMobile && !pagesWhereResizeDoesntCloseModal.has(currentPage)) {
			handleClickOutsideModal();
		}
	}, [
		currentPage,
		handleClickOutsideModal,
		isMobile,
		pagesWhereResizeDoesntCloseModal,
		preventHandleClickOutside
	]);

	useLayoutEffect(() => {
		if (!pagesWithAnimatedModal.has(currentPage)) return;

		const handleResize = () => {
			const audienceSortModal = audienceSortRef.current;

			if (audienceSortModal) {
				if (window.innerWidth <= SCREEN_WIDTHS.XS && wideModalSteps.has(currentStep)) {
					return;
				}

				audienceSortModal.style.opacity = "0";

				const audienceSortContent = audienceSortModal.firstElementChild as HTMLDivElement;
				let newMinHeight: string = "";

				audienceSortModal.style.height = "auto";

				if (currentStep !== audienceSortStepNames.initialStep) {
					audienceSortModal.style.maxWidth = "unset";
					audienceSortModal.style.width = "max-content";
				} else {
					audienceSortModal.style.removeProperty("min-width");
					audienceSortModal.style.removeProperty("width");
				}

				if (audienceSortContent) {
					if (
						window.innerWidth <= SCREEN_WIDTHS.SM &&
						currentStep === audienceSortStepNames.countrySelection
					) {
						newMinHeight = "100%";
					} else {
						newMinHeight = audienceSortContent.getBoundingClientRect().height + "px";
					}
				}

				audienceSortModal.style.removeProperty("height");
				audienceSortModal.style.removeProperty("width");
				audienceSortModal.style.removeProperty("max-width");
				audienceSortModal.style.minHeight = newMinHeight;

				audienceSortModal.style.opacity = "1";
			}

			return;
		};

		handleResize();
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, [children, currentPage, currentStep, isMobile, pagesWithAnimatedModal, wideModalSteps]);

	return createPortal(
		<DDXModal
			showModal={isModalOpen}
			handleCloseModal={handleClickOutsideModal}
			forceInlineModal={true}
			className="audience-sort-modal-container"
		>
			<div
				ref={audienceSortRef}
				className={
					`${className} ${currentStep} ${theme} ${currentPage}` + findModalAnimationClassname()
				}
			>
				{children}
			</div>
		</DDXModal>,
		document.body
	);
};

export default AudienceSortModal;
